/**
 * Period Comparison Component
 *
 * Compares this week's performance against last week to show trends.
 * Displays absolute click counts and percentage change with visual indicators.
 *
 * Time Calculation:
 * - "This week" = last 7 days including today
 * - "Last week" = 7 days immediately before "this week"
 *
 * Trend Classification:
 * - Up (green): > 5% increase
 * - Down (red): > 5% decrease
 * - Flat (gray): -5% to +5% change
 *
 * Use Cases:
 * - Track campaign performance week-over-week
 * - Identify traffic growth or decline early
 * - Compare before/after effects of changes
 *
 * @module components/charts/period-comparison
 */

import { useMemo, memo } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { formatNumber } from '@/client/lib/utils';

/** Daily statistics entry */
interface TimelineDataPoint {
  /** ISO date string (YYYY-MM-DD) */
  date: string;
  /** Number of clicks on this day */
  interactions: number;
}

interface PeriodComparisonProps {
  /** Array of daily statistics to analyze */
  data: TimelineDataPoint[];
}

/**
 * Period Comparison Component
 *
 * Shows week-over-week comparison with trend indicators.
 * Helps users understand if their link performance is improving.
 */
export const PeriodComparison = memo(function PeriodComparison({
  data: timelineData,
}: PeriodComparisonProps) {
  const comparison = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calculate this week (last 7 days including today)
    const thisWeekStart = new Date(today);
    thisWeekStart.setDate(thisWeekStart.getDate() - 6);

    // Calculate last week (7 days before this week)
    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);
    const lastWeekEnd = new Date(thisWeekStart);
    lastWeekEnd.setDate(lastWeekEnd.getDate() - 1);

    let thisWeekClicks = 0;
    let lastWeekClicks = 0;

    for (const stat of timelineData) {
      if (!stat.date) continue;
      const statDate = new Date(stat.date);
      statDate.setHours(0, 0, 0, 0);

      if (statDate >= thisWeekStart && statDate <= today) {
        thisWeekClicks += stat.interactions || 0;
      } else if (statDate >= lastWeekStart && statDate <= lastWeekEnd) {
        lastWeekClicks += stat.interactions || 0;
      }
    }

    // Calculate percentage change
    let percentChange = 0;
    let trend: 'up' | 'down' | 'flat' = 'flat';

    if (lastWeekClicks > 0) {
      percentChange = Math.round(((thisWeekClicks - lastWeekClicks) / lastWeekClicks) * 100);
      if (percentChange > 5) trend = 'up';
      else if (percentChange < -5) trend = 'down';
    } else if (thisWeekClicks > 0) {
      percentChange = 100;
      trend = 'up';
    }

    return {
      thisWeek: thisWeekClicks,
      lastWeek: lastWeekClicks,
      percentChange,
      trend,
    };
  }, [timelineData]);

  const TrendIcon =
    comparison.trend === 'up' ? TrendingUp : comparison.trend === 'down' ? TrendingDown : Minus;

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-semibold text-muted-foreground">Week over Week</h4>

      <div className="grid grid-cols-2 gap-4">
        {/* This Week */}
        <div className="rounded-lg bg-muted/30 p-4">
          <p className="text-xs text-muted-foreground mb-1">This Week</p>
          <p className="text-2xl font-bold tabular-nums">{formatNumber(comparison.thisWeek)}</p>
          <p className="text-xs text-muted-foreground">clicks</p>
        </div>

        {/* Last Week */}
        <div className="rounded-lg bg-muted/30 p-4">
          <p className="text-xs text-muted-foreground mb-1">Last Week</p>
          <p className="text-2xl font-bold tabular-nums">{formatNumber(comparison.lastWeek)}</p>
          <p className="text-xs text-muted-foreground">clicks</p>
        </div>
      </div>

      {/* Trend indicator */}
      <div
        className={`flex items-center justify-center gap-2 rounded-lg p-3 ${
          comparison.trend === 'up'
            ? 'bg-green-500/10 text-green-600'
            : comparison.trend === 'down'
              ? 'bg-red-500/10 text-red-600'
              : 'bg-muted/50 text-muted-foreground'
        }`}
      >
        <TrendIcon className="h-5 w-5" />
        <span className="font-semibold">
          {comparison.trend === 'up' && '+'}
          {comparison.percentChange}%
        </span>
        <span className="text-sm opacity-80">
          {comparison.trend === 'up'
            ? 'increase'
            : comparison.trend === 'down'
              ? 'decrease'
              : 'no change'}
        </span>
      </div>

      {comparison.thisWeek === 0 && comparison.lastWeek === 0 && (
        <p className="text-xs text-center text-muted-foreground">
          Share your link to start tracking performance
        </p>
      )}
    </div>
  );
});
